/* eslint-disable react/jsx-props-no-spreading */
import React, { createRef, useContext, useEffect } from 'react';
import {
  Route,
  RouteComponentProps,
  Switch,
  useHistory,
} from 'react-router-dom';
import {
  HeightProvider,
  TopBar as PatientsListingTopBar,
  WidgetHeader,
  AgGridWidgetWrapper,
  AppRoot,
  AppContent,
} from '@mummssoftware/common-ui';
import { print } from '@mummssoftware/utils/constants';
import PatientsListingPrint from '../Print';
import NotFound from '../NotFound';
import PatientsListingComponent from '../PatientsListing';
import { ListingContext } from '../Context/ListingContext';
import defaults from '../../translations/en.json';

type BaseAppProps = {
  embedded?: boolean;
  smallWidget?: boolean;
  appTitle?: string;
  currentUser: mumms.User;
  patientsList: mumms.patient[];
  online?: boolean;
  logout?: () => void;
  onClick?: () => void;
  onRemove?: () => void;
  doNotShow?: boolean;
  viewMode?: mumms.viewMode;
  onWidgetReady?: (arg: mumms.ComponentName) => void;
};

type AppProps = BaseAppProps & RouteComponentProps;

const mainPaths = ['/'];

const App = ({
  embedded,
  currentUser,
  smallWidget,
  patientsList,
  online,
  logout,
  viewMode,
  onWidgetReady,
  onClick,
  onRemove,
  doNotShow,
  appTitle,
}: BaseAppProps) => {
  const headerRef = createRef<HTMLElement>();

  const viewModePrint: boolean = viewMode === print;

  const { patientNumber } = useContext(ListingContext);
  const history = useHistory();

  useEffect(() => {
    if (patientNumber !== null) {
      history.push({
        pathname: `/${patientNumber}${history.location.search}`,
      });
      window.location.reload();
    }
  }, [patientNumber]);

  return (
    <AppRoot embedded={embedded}>
      {viewModePrint && (
        <PatientsListingPrint
          patientsList={patientsList}
          onWidgetReady={onWidgetReady}
        />
      )}

      {!viewModePrint && (
        <React.Fragment>
          {/* if not embedded in dashboard show a top bar when in stand alone mode */}
          {!embedded && (
            <React.Fragment>
              <PatientsListingTopBar
                ref={headerRef}
                title={defaults.title}
                disableSitePicker
                currentUser={currentUser}
                logout={logout}
              />
            </React.Fragment>
          )}

          <HeightProvider passedRef={headerRef}>
            {(height: any) => (
              <AppContent height={height} embedded={embedded}>
                <WidgetHeader
                  appTitle={appTitle || defaults.title}
                  versionInfo={`${defaults.title} v${process.env.REACT_APP_PATIENTS_LISTING_VERSION}, SmartChart v${process.env.REACT_APP_VERSION}`}
                  smallWidget={smallWidget}
                  onClick={onClick}
                  onRemove={onRemove}
                  doNotShow={doNotShow}
                />
                <Switch>
                  <AgGridWidgetWrapper
                    id="patients-listing-body"
                    smallWidget={smallWidget}
                  >
                    {patientsList ? (
                      <Route
                        path={mainPaths}
                        render={(props: any) => (
                          <PatientsListingComponent
                            embedded={embedded}
                            currentUser={currentUser}
                            patientsList={patientsList}
                            online={online}
                            smallWidget={smallWidget}
                            {...props}
                          />
                        )}
                      />
                    ) : null}
                    <Route
                      path="/"
                      render={(props: any) => <NotFound {...props} />}
                    />
                  </AgGridWidgetWrapper>
                </Switch>
              </AppContent>
            )}
          </HeightProvider>
        </React.Fragment>
      )}
    </AppRoot>
  );
};

export default App;
